import { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Card, CardBody, CardHeader, CardTitle } from 'reactstrap'

import TableView from '../TableViews/TableView'
import defaultColumn from './Components/DefaultColumn'

export const unwrapPage = (response, pageSize) => {
  let payload = response

  while (
    payload &&
    payload.data !== undefined &&
    !Array.isArray(payload) &&
    !payload.result &&
    !payload.rows &&
    !payload.items
  ) {
    payload = payload.data
  }

  if (Array.isArray(payload)) {
    return {
      rows: payload,
      total: payload.length,
      totalPage: Math.max(Math.ceil(payload.length / pageSize), 1),
      manualPagination: false
    }
  }

  const rows = payload?.result || payload?.rows || payload?.items || []
  const pagination = payload?.pagination || payload?.meta?.pagination || {}
  const effectivePageSize = Number(
    pagination.limit ?? pagination.pageSize ?? payload?.limit ?? payload?.size ?? pageSize
  ) || pageSize
  const total = Number(
    pagination.total ??
    pagination.totalRecords ??
    payload?.total ??
    payload?.totalRows ??
    payload?.max ??
    payload?.count ??
    rows.length
  ) || 0
  const explicitTotalPage =
    pagination.pages ??
    pagination.totalPages ??
    payload?.totalPage ??
    payload?.pageCount
  const totalPage = Number(explicitTotalPage) || Math.max(Math.ceil(total / effectivePageSize), 1)
  const manualPagination = explicitTotalPage !== undefined || total > rows.length

  return { rows, total, totalPage, manualPagination }
}

function CommonListApiPaginated ({
  getData,
  mapData = (rows) => rows,
  columns,
  header,
  subtitle,
  menu,
  Add,
  addDisabled = false,
  customFunc,
  footerChild,
  reloadKey,
  initialPageSize = 10,
  loadingLabel = 'Loading data...',
  emptyLabel = 'No data found',
  onError,
  embedded = false,
  enabled = true,
  query = {},
  searchDebounceMs = 350,
  searchPlaceholder = 'Cari semua data...',
  onData
}) {
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(initialPageSize)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [rawRows, setRawRows] = useState([])
  const [total, setTotal] = useState(0)
  const [totalPage, setTotalPage] = useState(1)
  const [manualPagination, setManualPagination] = useState(true)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const getDataRef = useRef(getData)
  const queryRef = useRef(query)
  const onDataRef = useRef(onData)
  const onErrorRef = useRef(onError)
  const requestRef = useRef(0)
  const resetRef = useRef('')
  const clientLoadKeyRef = useRef('')
  const queryKey = useMemo(() => JSON.stringify(query || {}), [query])
  const resetKey = `${reloadKey ?? ''}:${queryKey}:${search}`
  const rows = useMemo(() => mapData(rawRows), [mapData, rawRows])

  getDataRef.current = getData
  queryRef.current = query
  onDataRef.current = onData
  onErrorRef.current = onError

  useEffect(() => {
    if (searchInput === search) return undefined

    const timer = window.setTimeout(() => setSearch(searchInput.trim()), searchDebounceMs)
    return () => window.clearTimeout(timer)
  }, [search, searchDebounceMs, searchInput])

  useEffect(() => {
    if (resetRef.current !== resetKey && page !== 0) {
      resetRef.current = resetKey
      setPage(0)
      return undefined
    }
    resetRef.current = resetKey

    if (!enabled || !getDataRef.current) {
      setRawRows([])
      setTotal(0)
      setTotalPage(1)
      setLoading(false)
      setError('')
      return undefined
    }

    if (!manualPagination && clientLoadKeyRef.current === resetKey) {
      return undefined
    }

    let mounted = true
    const requestId = requestRef.current + 1
    requestRef.current = requestId

    const loadPage = async () => {
      setLoading(true)
      setError('')

      try {
        const response = await getDataRef.current({
          page: page + 1,
          pageSize,
          search,
          ...(queryRef.current || {})
        })
        const result = unwrapPage(response, pageSize)

        if (!mounted || requestRef.current !== requestId) return

        setRawRows(result.rows)
        setTotal(result.total)
        setTotalPage(result.totalPage)
        setManualPagination(result.manualPagination)
        clientLoadKeyRef.current = result.manualPagination ? '' : resetKey
        onDataRef.current?.(response, result)

        if (page > 0 && page >= result.totalPage) {
          setPage(Math.max(result.totalPage - 1, 0))
        }
      } catch (loadError) {
        if (!mounted || requestRef.current !== requestId) return

        const message = loadError.response?.data?.message || loadError.message || 'Failed to load data'
        setRawRows([])
        setTotal(0)
        setTotalPage(1)
        setError(message)
        await onErrorRef.current?.(loadError)
      } finally {
        if (mounted && requestRef.current === requestId) setLoading(false)
      }
    }

    loadPage()
    return () => {
      mounted = false
    }
  }, [enabled, manualPagination, page, pageSize, resetKey, search])

  const menuContent = menu || (Add instanceof Function
    ? (
      <div className="text-center">
        <button
          type="button"
          className="btn btn-success"
          onClick={Add}
          disabled={addDisabled}
        >
          <i className="fa-solid fa-pen-to-square" />&nbsp;&nbsp; Tambah
        </button>
      </div>
      )
    : null)

  const table = (
    <>
      {error ? <Alert color="danger"><i className="fa fa-exclamation-circle me-1" />{error}</Alert> : null}
      {menuContent}
      <TableView
        data={rows}
        pageState={page}
        pageStateSet={setPage}
        pageSizeState={pageSize}
        pageSizeStateSet={setPageSize}
        pageCount={totalPage}
        totalRows={total}
        manualPagination={manualPagination}
        columns={columns || defaultColumn}
        filename={header}
        customFunc={customFunc}
        emptyLabel={emptyLabel}
        globalFilterState={searchInput}
        globalFilterStateSet={setSearchInput}
        searchPlaceholder={searchPlaceholder}
        loading={loading}
        loadingLabel={loadingLabel}
      />
      {footerChild ?? null}
    </>
  )

  if (embedded) return <div>{table}</div>

  return (
    <Card className="shadow-md">
      <CardHeader>
        <CardTitle tag="h5" className="title mb-1">{header || 'List'}</CardTitle>
        {subtitle ? <p className="text-subtitle text-muted mb-0">{subtitle}</p> : null}
      </CardHeader>
      <CardBody>{table}</CardBody>
    </Card>
  )
}

export default CommonListApiPaginated
